import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import confirm from 'components/confirm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEllipsisV, faRotate } from '@fortawesome/pro-solid-svg-icons';
import { MenuItem } from 'react-bootstrap';

import ActionsMenu from 'components/actions_menu';
import ClickableIcon from 'components/clickable_icon';
import ContactDetails from 'bundles/backoffice/contacts/details';
import ContactIcon from 'bundles/backoffice/contacts/icon';
import ReplaceContactModal from 'bundles/backoffice/contacts/replace_modal';
import { clickAsLink } from 'helpers/link_helpers';

export function Contact({
  confirmDeletion,
  contactRole,
  externalEditing,
  formModalComponent,
  forRecord,
  readOnly,
  renderLinks,
  showEmployer = true,
  onDelete,
  onReplace,
  onSave,
}) {
  const [showContactEditModal, setShowContactEditModal] = useState();
  const [showBusinessEditModal, setShowBusinessEditModal] = useState();
  const [showReplaceContactModal, setShowReplaceContactModal] = useState();

  const contact = useMemo(() => contactRole.contact, [contactRole]);

  let business;
  if (showEmployer) {
    business = contact.business;
  }

  const FormModal = formModalComponent;

  function handleEdit(event) {
    if (externalEditing) {
      clickAsLink(window, contact.path, event);
    } else {
      setShowContactEditModal(true);
    }
  }

  function handleBusinessEdit(event) {
    if (externalEditing) {
      clickAsLink(window, business.path, event);
    } else {
      setShowBusinessEditModal(true);
    }
  }

  const otherOptions = [];
  if (business) {
    otherOptions.push(
      <MenuItem
        key="edit-business"
        name="edit-business-button"
        onClick={handleBusinessEdit}
      >
        <FontAwesomeIcon fixedWidth icon={faEdit} /> Edit Company
      </MenuItem>
    );
  }

  otherOptions.push(
    <MenuItem key="replace" onClick={() => setShowReplaceContactModal(true)}>
      <FontAwesomeIcon fixedWidth icon={faRotate} /> Replace
      <span className="ml-4 small text-muted">(Recommended)</span>
    </MenuItem>
  );

  function deleteContact() {
    if (confirmDeletion) {
      confirm('Are you sure you want to remove this contact?', {
        buttonLabel: 'Remove',
        buttonStyle: 'danger',
        title: `Remove Contact`
      }).then(() => {
        onDelete(contactRole);
      });
    } else {
      onDelete(contactRole);
    }
  }

  return (
    <div className="contact d-flex flex-1 p-4">
      <ContactIcon contact={contact} />

      <ContactDetails
        contact={contact}
        contactRole={contactRole}
        renderLinks={renderLinks}
        showEmployer={showEmployer}
      />

      {!readOnly &&
        <ActionsMenu
          deleteLabel="Remove"
          icon={<ClickableIcon icon={faEllipsisV} size="xl"/>}
          id={contact.id}
          otherOptions={otherOptions}
          onDelete={deleteContact}
          onShowModal={handleEdit}
        />
      }

      <FormModal
        contact={contact}
        forRecord={forRecord}
        show={showContactEditModal}
        onHide={() => setShowContactEditModal(false)}
        onSave={onSave}
      />

      <FormModal
        contact={business || {}}
        forRecord={forRecord}
        show={showBusinessEditModal}
        onHide={() => setShowBusinessEditModal(false)}
        onSave={onSave}
      />

      <ReplaceContactModal
        contactRole={contactRole}
        newContactVisibility="shared"
        show={showReplaceContactModal}
        onChange={newContactRole => onReplace(contactRole, newContactRole)}
        onHide={() => setShowReplaceContactModal(false)}
      />
    </div>
  );
}

Contact.propTypes = {
  confirmDeletion: PropTypes.bool,
  contactRole: PropTypes.shape({
    contact: PropTypes.shape({
      business: PropTypes.shape({
        name: PropTypes.string,
      }),
      isBusiness: PropTypes.bool,
      email: PropTypes.string,
      name: PropTypes.string,
      path: PropTypes.string,
      phoneFormatted: PropTypes.string,
      licenseNumebr: PropTypes.string,
    }).isRequired,
  }),
  externalEditing: PropTypes.bool,
  formModalComponent: PropTypes.func.isRequired,
  forRecord: PropTypes.object,
  readOnly: PropTypes.bool,
  renderLinks: PropTypes.bool,
  showEmployer: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onSave: PropTypes.func,
};

export default Contact;
